import { FormStore, FormValidator, IJsonSchema, getValidationSchema } from '@estee/elc-form';
import { action, computed, observable } from 'mobx';
import { StringSchema, string as yupString } from 'yup';
import { FIELD_TYPE } from '../../../constants/Constants';
import Config, { IPhonePattern, IPhoneCountryPattern } from '../Config';
import { ITranslationSchema } from '../../../interfaces/ITranslationFields';
import { FormField, FormSchema, PhoneField, ValidationRules  } from '../../../views/GenericForm/types';
import { DataField } from './DataField';
import { getCountryAbbr } from '../../../utils/AddressFormUtils';

interface ValidationFieldsMap { 
  [field: string]: ValidationRules;
}

export interface ICustomValidator {
  pattern: string;
  errorMessage: string;
}

export interface ISchemaCustomValidator {
  customValidation?: ICustomValidator, required?: string
}

interface IMapInputFieldData {
  [key: string]: DataField;
}

type CustomValidator = (configs: ICustomValidator) => StringSchema;

interface IBaseFormStoreOptions<T> {
  initialValues: T;
}

export interface IBaseFormAttributes {
  [field: string]: string | number;
}

export interface IFormStoreOptions<T> {
  initialValues: T;
}

export type ValidationSchema = {
  required?: string;
  pattern?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
} | {
  customValidation: ICustomValidator
};

/*
  Should handle form store for any form field, currently it supports the following fields:
    - simple text input
    - phone input
    - email built-in validation support
  Expand as more field types are going to be supported.
*/
class GenericFormStore<T extends IBaseFormAttributes> extends FormStore<T> {

  @observable public formSchema: FormSchema;

  @observable private translations: ITranslationSchema = {};

  @observable config: Config;

  constructor(config: Config, options: IBaseFormStoreOptions<T>) {
    super({
      formValidator: new FormValidator(),
      initialValues: options.initialValues,
    });
    (this.formValidator as FormValidator).registerValidation('customValidation', this.validateCustomPattern);
    this.config = config;
  }

  // helpful when additional configurations are needed prior running the address validation out of a view form.
  // protected abstract runPreValidationHandling(): void;

  @computed get countryAbbr(): string {
    return getCountryAbbr(Number(this.values[FIELD_TYPE.COUNTRY_ID]));
  }

  @computed get globalPhoneCountriesPattern(): IPhoneCountryPattern {
    return this.config.phonePatterns;
  }

  protected getCustomValidation(formField: FormField, errorMessage: string, requiredMessage: string): ISchemaCustomValidator {
    let validation;
  
    switch (formField.type) {
      case 'tel': {
        const phonePattern = this.getPhonePatternFromConfig(formField);

        if (phonePattern) {
          validation = {
            customValidation: {
              errorMessage,
              pattern: phonePattern.pattern,
            },
            required: requiredMessage,
          };
        }
        break;
      }
    }

    return validation;
  }

  @computed get fieldsValidations():  ValidationFieldsMap {
    return this.formSchema?.fields.reduce((acc, current) => {
      return {
        ...acc,
        [current.id]: current.validation,
      };
    }, {});
  }

  @computed get jsonSchema(): IJsonSchema {
    const required = (Object.keys(this.fieldsValidations)).reduce((acc: string[], current) => {
      if (this.fieldsValidations[current].required) {
        return [...acc, current];
      } else {
        return acc;
      }
    }, []);

    return {
      properties: this.fieldsValidations,
      required: required,
    };
  }

  @computed get inputsData(): IMapInputFieldData {
    const properties = this.jsonSchema?.properties || {};

    return (Object.keys(properties) as Array<keyof typeof properties>).reduce((acc, item) => ({
      ...acc,
      [item]: new DataField({
        field: this.formSchema.fields.find(field => field.id === item), 
        translations: this.translations,
      }),
    }), {});
  }

  private setSchemaValidations(): void {
    const excludeFromValidation = [FIELD_TYPE.COUNTRY_ID];

    Object.keys(this.jsonSchema?.properties).forEach((schemaFieldKey: string) => {
      const schemaField = this.jsonSchema?.properties[schemaFieldKey];
      const field = schemaField ? schemaFieldKey : null;

      if (field && !excludeFromValidation.find(item => item === schemaFieldKey)) {
        const validations = getValidationSchema(this.jsonSchema, schemaFieldKey, {
          minLengthMessage: '',
          patternMessage: '',
          requiredMessage: '',
        }) || {};
        this.setFieldValidations(field, validations);
      }
    });
  }

  getValidations(fieldType: string): ValidationSchema {
    const inputData = this.inputsData[fieldType];

    let validations: ValidationSchema | null = this.getCustomValidation(  
      inputData.field,
      inputData.validationMessages.patternMessage,
      inputData.field.validation.required ? inputData.validationMessages.requiredMessage : '',
    );

    if (!validations) {
      validations = this.getValidationByPattern(inputData, fieldType, inputData.field.validation.pattern);
    }

    return validations;
  }

  private getValidationByPattern(inputData: DataField, fieldId:string,  pattern: string): ValidationSchema {
    switch (pattern) {
      case 'email':
        return inputData.emailValidation;

      default:
        return getValidationSchema(
          this.jsonSchema,
          fieldId,
          inputData.validationMessages,
        );
    }
  }

  @action
  public setFormSchema(schema: FormSchema): void {
    this.formSchema = schema;
  }

  public validateAddressBySchema(formSchema: FormSchema): boolean {
    this.setFormSchema(formSchema);
    this.setSchemaValidations();
    this.setPhoneConfigValidations();
    this.runValidationSchemaSync();

    return this.isValid;
  }

  @action
  public setTranslations(translations: ITranslationSchema): void {
    this.translations = translations;
  }

  private getPhoneCountryCodesFromSchema(field: string): IPhonePattern[] {
    return (<PhoneField> this.inputsData[field]?.field).countryCodesPatterns?.map(item => {
      return {
        country: item.country,
        countryCallingCode: item.phoneCode,
        pattern: item.pattern,
      };
    }) || [];
  }

  private setPhoneConfigValidations(): void {
    const phoneFields = this.formSchema?.fields?.filter(item => item.type === 'tel');
    phoneFields.forEach(phoneField => {
      const validation = this.fieldsValidations[phoneField.id];
      if (validation) {
        const phonePatternBySchema = this.getPhonePatternFromConfig(phoneField);

        if (phonePatternBySchema) {
          const validations = {
            customValidation: {
              errorMessage: '',
              pattern: phonePatternBySchema.pattern,
            },
          };
          this.setFieldValidations(phoneField.id, validations);
        }
      }
    });
  }

  private getPhonePatternFromConfig(formField: FormField): IPhonePattern | null {
    if (formField.type === 'tel') {
      const phonePatterns = this.getPhoneCountryCodesFromSchema(formField.id);
      return this.getPhonePatternByFormCountryId(phonePatterns) || this.globalPhoneCountriesPattern[this.countryAbbr];
    }
  }

  private getPhonePatternByFormCountryId(
    countryCodes: IPhonePattern[] = [],
  ): IPhonePattern {  
    return countryCodes.find(item => item.country === this.countryAbbr);
  }

  private validateCustomPattern: CustomValidator = ({
    pattern = '',
    errorMessage = '',
  }) => {
    const regExp = new RegExp(pattern);
    return yupString().matches(regExp, errorMessage);
  };
}

export default GenericFormStore;
